import { Routes, Route, BrowserRouter as Router } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import React from 'react'; // Add explicit React import
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import { DebugPanel } from './components/debug/DebugPanel';
import { BookingProvider } from './contexts/BookingContext';
import { PlausibleAnalytics } from './components/PlausibleAnalytics';
import { PageSEO } from './components/SEO/PageSEO';

// Layout Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback';

// Regular Pages
import Home from './pages/Home';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Datenschutz from './pages/Datenschutz';
import AGB from './pages/AGB';
import Impressum from './pages/Impressum';
import BookingFlow from './pages/BookingFlow';
import StudioPublicPage from './pages/StudioPublicPage';
import AdminDashboard from './pages/AdminDashboard';
import NotFound from './pages/NotFound';
import FeedbackPage from './pages/FeedbackPage';
import Favorites from './pages/Favorites';
import StudioInsightsPage from './pages/StudioInsightsPage';
import Subscriptions from './pages/Subscriptions';
import Pricing from './pages/Pricing';
import BillingPage from './pages/BillingPage';
import Unauthorized from './pages/Unauthorized';
import WaitlistPage from './pages/WaitlistPage';
import Index from './pages/Index'; // Keep for backward compatibility
import PartnerPage from './pages/PartnerPage';
import AdminSetup from './pages/AdminSetup';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword'; // Import the new page

// Global error handler
import { logError } from './utils/error-logger';
import { logger } from './services/logger';

function App() {
  return (
    <AuthProvider>
      <Router>
        <BookingProvider>
          {/* Default SEO Meta Tags */}
          <PageSEO
            title="CHRONA – Dein Termin. Deine Zeit."
            description="Finde, buche und verwalte Beauty-Termine in ganz Deutschland – schnell, einfach, mobil."
            keywords="Termine, Beauty, Spa, Massage, Friseur, Buchung, Studio, Kundenportal, Kalender"
            canonicalPath="/"
            ogImage="/lovable-uploads/bbbd313b-d6bd-4b33-af58-258cd00a4dc3.png"
            ogType="website"
            twitterCard="summary_large_image"
            twitterSite="@chrona_app"
          />
          
          {/* Analytics */}
          <PlausibleAnalytics 
            domain="chrona.it" 
            scriptSrc="https://plausible.io/js/script.js"
            trackLocalhost={false}
          />
          
          <div className="App bg-[#212121] min-h-screen">
            <React.Fragment>
              <Routes>
                {/* Waitlist page - outside of normal layout */}
                <Route path="/waitlist" element={<WaitlistPage />} />
                
                {/* Forgot Password page - outside of normal layout */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                
                {/* Change Password page - outside of normal layout */}
                <Route path="/change-password" element={<ChangePassword />} />
                
                {/* Normal layout with header/footer */}
                <Route path="/" element={<Layout />}>
                  {/* Home page as the main landing page */}
                  <Route index element={<Home />} />
                  
                  {/* Legacy Index route for backward compatibility */}
                  <Route path="index" element={<Index />} />
                  
                  <Route path="pricing" element={<Pricing />} />
                  <Route path="auth" element={<Auth />} />
                  <Route path="auth/callback" element={<AuthCallback />} />
                  <Route path="login" element={<Auth />} />
                  <Route path="register" element={<Auth />} />
                  <Route path="datenschutz" element={<Datenschutz />} />
                  <Route path="agb" element={<AGB />} />
                  <Route path="impressum" element={<Impressum />} />
                  <Route path="feedback" element={<FeedbackPage />} />
                  <Route path="unauthorized" element={<Unauthorized />} />
                  <Route path="partner" element={<PartnerPage />} />
                  
                  {/* Public Studio Page */}
                  <Route path="studio/:slug" element={<StudioPublicPage />} />
                  
                  {/* Category and Location Pages */}
                  <Route path="category/:categorySlug" element={<Home />} />
                  <Route path="location/:locationSlug" element={<Home />} />

                  {/* Protected routes */}
                  <Route path="dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="profile" element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } />
                  <Route path="favorites" element={
                    <ProtectedRoute>
                      <Favorites />
                    </ProtectedRoute>
                  } />
                  <Route path="subscriptions" element={
                    <ProtectedRoute>
                      <Subscriptions />
                    </ProtectedRoute>
                  } />
                  <Route path="account/billing" element={
                    <ProtectedRoute>
                      <BillingPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin routes */}
                  <Route path="admin" element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin-setup" element={<AdminSetup />} />
                  
                  {/* Legacy Studio URLs - kept for backward compatibility */}
                  <Route path="studios/:slug" element={<StudioPublicPage />} />
                  
                  <Route path="studio-dashboard/insights" element={
                    <ProtectedRoute requiredRole="studio_owner">
                      <StudioInsightsPage />
                    </ProtectedRoute>
                  } />

                  {/* Booking routes */}
                  <Route path="booking/:studioId" element={<BookingFlow />} />
                  <Route path="booking/complete" element={<BookingFlow />} />
                  <Route path="booking" element={<BookingFlow />} />

                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </React.Fragment>
          </div>
          <Toaster />
          <DebugPanel showOnlyInDev={process.env.NODE_ENV === 'development'} />
        </BookingProvider>
      </Router>
    </AuthProvider>
  );
}

// Catch any errors in the component tree
const AppWithErrorBoundary = () => {
  try {
    return <App />;
  } catch (error) {
    logger.error(error instanceof Error ? error : new Error(String(error)), { 
      metadata: { component: 'App', fatal: true }
    });
    
    return (
      <div className="min-h-screen bg-[#212121] flex flex-col items-center justify-center p-4 text-white">
        <h1 className="text-2xl font-bold mb-4">Etwas ist schiefgelaufen</h1>
        <p className="mb-6">Wir entschuldigen uns für die Unannehmlichkeiten. Bitte lade die Seite neu.</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#2C6E49] rounded-lg hover:bg-[#4C956C] transition-colors"
        >
          Seite neu laden
        </button>
      </div>
    );
  }
};

export default AppWithErrorBoundary;
